import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { brl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Painel de Aditivos (orçamentos extras aprovados) — aparece na aba Financeiro
 * de cada obra. Fluxo:
 *   Orçamento base (obra.valor_total)
 *     + Aditivo 1 (aprovado)
 *     + Aditivo N ...
 *   ─────────────────────────────
 *   Total APROVADO da obra
 *
 * Cliente/arquiteto: read-only + não vê rejeitados.
 * Admin/gestor/financeiro/operacao: pode criar, aprovar, rejeitar, remover.
 */
const ADITIVO_COR = "#7fb0e0";

export default function AditivosPanel({ obraId, canFin, api, onReload }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: "", descricao: "", valor: "", numero_pedido: "", status: "aprovado" });
  const [err, setErr] = useState("");

  const token = localStorage.getItem("cps_token");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/obras/${obraId}/aditivos`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) { /* silencioso pra não poluir UI */ }
  }, [obraId, token]);

  useEffect(() => { load(); }, [load]);

  const adicionar = async () => {
    setErr("");
    const nome = form.nome.trim();
    const valor = parseFloat(String(form.valor).replace(",", "."));
    if (!nome) return setErr("Informe o nome do aditivo");
    if (isNaN(valor) || valor <= 0) return setErr("Valor inválido");
    setSaving(true);
    try {
      // Cria (nasce pendente no backend) e — se admin escolheu já decidir — patch imediato
      const r = await api("post", `/obras/${obraId}/medicoes/orcamento-extra`, {
        nome, valor,
        descricao: form.descricao.trim() || undefined,
        numero_pedido: form.numero_pedido.trim() || undefined,
      });
      const aid = r?.orcamento?.id;
      if (aid && form.status !== "pendente") {
        await api("patch", `/obras/${obraId}/aditivos/${aid}`, { status: form.status });
      }
      setForm({ nome: "", descricao: "", valor: "", numero_pedido: "", status: "aprovado" });
      setShowForm(false);
      await load();
      onReload && onReload();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const aprovar = async (aid) => {
    await api("patch", `/obras/${obraId}/aditivos/${aid}`, { status: "aprovado" });
    await load(); onReload && onReload();
  };
  const rejeitar = async (aid) => {
    const motivo = window.prompt("Motivo da rejeição (opcional):", "");
    if (motivo === null) return; // cancelou
    await api("patch", `/obras/${obraId}/aditivos/${aid}`, { status: "rejeitado", motivo_rejeicao: motivo || undefined });
    await load(); onReload && onReload();
  };
  const reabrir = async (aid) => {
    await api("patch", `/obras/${obraId}/aditivos/${aid}`, { status: "pendente" });
    await load(); onReload && onReload();
  };
  const remover = async (aid) => {
    if (!window.confirm("Remover este aditivo permanentemente?")) return;
    await api("delete", `/obras/${obraId}/medicoes/orcamento-extra/${aid}`);
    await load(); onReload && onReload();
  };

  if (!data) return null;
  const { orcamento_base, aditivos, total_aditivos_aprovados, total_aprovado, total_pendente, qtd_pendentes, qtd_rejeitados } = data;
  const isEmpty = !aditivos?.length;

  return (
    <div className="panel" style={{ marginBottom: 24 }} data-testid="aditivos-panel">
      <div className="panel-head">
        <div>
          <div className="panel-eyebrow">Orçamento contratual</div>
          <h3 className="panel-title">Serviços Adicionais <em>da obra</em></h3>
        </div>
        {canFin && (
          <button className="btn btn-sm" onClick={() => { setShowForm(!showForm); setErr(""); }} data-testid="aditivo-add-btn">
            {showForm ? "Cancelar" : "+ Novo Serviço Adicional"}
          </button>
        )}
      </div>

      {/* Form de criação */}
      {showForm && canFin && (
        <div style={{ padding: 14, background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.25)", marginBottom: 14 }} data-testid="aditivo-form">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 10, marginBottom: 10 }}>
            <input
              type="text" className="input" placeholder="Ex: Aditivo 1 — Bancada extra do lavabo"
              value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
              data-testid="aditivo-nome" autoFocus
            />
            <input
              type="text" className="input" placeholder="R$ 0,00" inputMode="decimal"
              value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })}
              data-testid="aditivo-valor"
            />
          </div>
          <input
            type="text" className="input"
            placeholder="Nº do Pedido / Orçamento (ex: OR-2026-045)"
            value={form.numero_pedido} onChange={e => setForm({ ...form, numero_pedido: e.target.value })}
            data-testid="aditivo-numero-pedido"
            style={{ marginBottom: 10 }}
          />
          <textarea
            className="input" rows={2} placeholder="Descrição (opcional) — o que foi negociado?"
            value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
            data-testid="aditivo-descricao"
            style={{ marginBottom: 10 }}
          />
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase" }}>Decisão:</span>
            {[
              { v: "aprovado", label: "Aprovado", color: "#5fa86c" },
              { v: "pendente", label: "Pendente", color: "#b8935a" },
              { v: "rejeitado", label: "Rejeitado", color: "#c47a7a" },
            ].map(opt => (
              <label key={opt.v} data-testid={`aditivo-status-${opt.v}`} style={{
                display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                padding: "4px 10px", fontSize: 12, letterSpacing: ".1em", textTransform: "uppercase",
                border: `1px solid ${form.status === opt.v ? opt.color : "var(--line)"}`,
                color: form.status === opt.v ? opt.color : "var(--ink-3)",
                background: form.status === opt.v ? `${opt.color}14` : "transparent",
              }}>
                <input type="radio" name="aditivo-status" value={opt.v}
                  checked={form.status === opt.v}
                  onChange={() => setForm({ ...form, status: opt.v })}
                  data-testid={`aditivo-status-radio-${opt.v}`}
                  style={{ display: "none" }} />
                {opt.label}
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-sm btn-success" onClick={adicionar} disabled={saving} data-testid="aditivo-confirm">
              {saving ? "Salvando…" : "Salvar Serviço Adicional"}
            </button>
          </div>
          {err && <div style={{ marginTop: 8, color: "#e35d5d", fontSize: 12 }} data-testid="aditivo-err">⚠ {err}</div>}
        </div>
      )}

      {/* Tabela resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 10, columnGap: 16, fontSize: 14, alignItems: "center" }}>
        <div style={{ color: "var(--ink-2)" }}>Orçamento base</div>
        <div data-testid="aditivo-base" style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink)", fontWeight: 600 }}>{brl(orcamento_base)}</div>

        {isEmpty ? (
          <div style={{ gridColumn: "1/-1", color: "var(--ink-3)", fontSize: 13, padding: "12px 0", fontStyle: "italic" }}>
            Nenhum serviço adicional cadastrado ainda.
          </div>
        ) : (
          aditivos.map((a) => {
            const statusColor = a.status === "aprovado" ? "#5fa86c" : a.status === "rejeitado" ? "#c47a7a" : "#b8935a";
            return (
              <React.Fragment key={a.id}>
                <div style={{ gridColumn: "1/-1", borderTop: "1px solid rgba(127,176,224,0.18)", marginTop: 2 }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4, borderLeft: `3px solid ${a.status === "rejeitado" ? "var(--line)" : ADITIVO_COR}`, paddingLeft: 10, background: a.status === "rejeitado" ? "transparent" : "rgba(127,176,224,0.05)" }} data-testid={`aditivo-row-${a.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".2em", color: "#0d0d0f", background: ADITIVO_COR, padding: "2px 7px", borderRadius: 2 }} data-testid={`aditivo-tag-${a.id}`}>ADITIVO</span>
                    <span style={{ color: "var(--ink)", fontWeight: 500 }}>{a.nome}</span>
                    <span style={{
                      fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase",
                      color: statusColor, border: `1px solid ${statusColor}`, padding: "1px 8px", borderRadius: 2,
                    }} data-testid={`aditivo-status-${a.id}`}>{a.status}</span>
                    {a.data_aprovacao && a.status === "aprovado" && (
                      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>aprovado {a.data_aprovacao}</span>
                    )}
                    {a.numero_pedido && (
                      <span data-testid={`aditivo-num-${a.id}`} style={{
                        fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase",
                        color: "var(--ink-3)", border: "1px solid var(--line)", padding: "1px 8px",
                        fontFamily: "'Fira Code', monospace",
                      }}>#{a.numero_pedido}</span>
                    )}
                  </div>
                  {a.descricao && <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{a.descricao}</span>}
                  {a.motivo_rejeicao && <span style={{ fontSize: 12, color: "#c47a7a", fontStyle: "italic" }}>Rejeitado: {a.motivo_rejeicao}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", color: a.status === "aprovado" ? "var(--ink)" : "var(--ink-3)", fontWeight: a.status === "aprovado" ? 600 : 400, textDecoration: a.status === "rejeitado" ? "line-through" : "none" }}>
                    {brl(a.valor)}
                  </span>
                  {canFin && (
                    <span style={{ display: "flex", gap: 4 }}>
                      {a.status !== "aprovado" && <button className="btn btn-sm" onClick={() => aprovar(a.id)} data-testid={`aditivo-approve-${a.id}`} title="Aprovar">✓</button>}
                      {a.status !== "rejeitado" && <button className="btn btn-sm btn-ghost" onClick={() => rejeitar(a.id)} data-testid={`aditivo-reject-${a.id}`} title="Rejeitar" style={{ color: "#c47a7a" }}>✗</button>}
                      {a.status !== "pendente" && <button className="btn btn-sm btn-ghost" onClick={() => reabrir(a.id)} data-testid={`aditivo-reopen-${a.id}`} title="Voltar pra pendente">↺</button>}
                      <button className="btn btn-sm btn-ghost" onClick={() => remover(a.id)} data-testid={`aditivo-del-${a.id}`} title="Remover" style={{ color: "#c47a7a" }}>×</button>
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })
        )}

        {/* Totais */}
        {!isEmpty && (
          <>
            <div style={{ gridColumn: "1/-1", borderTop: "2px solid rgba(184,147,90,0.4)", marginTop: 10 }} />
            <div style={{ color: "var(--ink-2)", fontSize: 13 }}>+ Aditivos aprovados</div>
            <div data-testid="aditivo-total-aprov" style={{ fontVariantNumeric: "tabular-nums", color: "#5fa86c", fontWeight: 600 }}>{brl(total_aditivos_aprovados)}</div>
            {total_pendente > 0 && (
              <>
                <div style={{ color: "var(--ink-3)", fontSize: 12 }}>({qtd_pendentes} pendente(s), aguardando sua decisão)</div>
                <div data-testid="aditivo-total-pend" style={{ fontVariantNumeric: "tabular-nums", color: "#b8935a", fontSize: 12 }}>{brl(total_pendente)}</div>
              </>
            )}
            <div style={{ color: "var(--gold)", fontWeight: 600, fontSize: 15 }}>Total APROVADO da obra</div>
            <div data-testid="aditivo-total-final" style={{ fontVariantNumeric: "tabular-nums", color: "var(--gold)", fontWeight: 700, fontSize: 18, fontFamily: "'Fraunces', serif" }}>
              {brl(total_aprovado)}
            </div>
            {qtd_rejeitados > 0 && (
              <div style={{ gridColumn: "1/-1", fontSize: 11, color: "var(--ink-3)", fontStyle: "italic", marginTop: 4 }}>
                {qtd_rejeitados} aditivo(s) rejeitado(s) preservado(s) no histórico
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
